import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import api, { isLimitError } from '../api/api';
import { useCreateContext } from '../product/CreateContext';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { useCreateFlow, usePrefill } from './create/flow';
import './media.css';

import alone from '../audio/alone.mp3';
import inspiringPiano from '../audio/inspiring-piano.mp3';
import kugelsicher from '../audio/kugelsicher.mp3';
import stylishDeepElectronic from '../audio/stylish-deep-electronic.mp3';
import summerCoctail from '../audio/summer-coctail.mp3';
import whipAfroDancehall from '../audio/whip-afro-dancehall.mp3';
import fontFile from '../assets/fonts/OpenSans-Regular.ttf';

// FFmpeg (WebAssembly) renders template videos in the browser; loaded on first use
const ffmpeg = new FFmpeg();
let loaded = false;

const PRESET_AUDIO = [
    { id: 'select', name: 'Choose a track…', url: '' },
    { id: 'music1', name: 'Alone', url: alone },
    { id: 'music2', name: 'Inspiring Piano', url: inspiringPiano },
    { id: 'music3', name: 'Kugelsicher', url: kugelsicher },
    { id: 'music4', name: 'Stylish Deep Electronic', url: stylishDeepElectronic },
    { id: 'music5', name: 'Summer Cocktail', url: summerCoctail },
    { id: 'music6', name: 'Whip Afro Dancehall', url: whipAfroDancehall },
];

const VIDEO_TYPES = [
    {
        title: 'Text over image',
        description: 'This format has a text over an image.',
        hook: 'A single line describing the image related to the product or service.',
        exampleHooks: [
            'People with these hobbies have the purest hearts',
            'If you do these hobbies, you are built different',
            'People with these hobbies have the deepest thinkers',
            'People with these hobbies see the world differently',
        ],
    },
    {
        title: 'Text Wall over video',
        description: 'This format has a text wall over a video.',
        hook: 'A 30-40 word hook describing the text wall related to the product or service.',
        exampleHooks: [
            'This is an example hook for a text wall video',
            'This is an example hook for a text wall video',
        ],
    },
    {
        title: 'Animated Captions over video',
        description: 'This format has animated captions over a video.',
        hook: 'A 30-40 word hook describing the animated captions related to the product or service.',
        exampleHooks: [
            'This is an example hook for a animated captions video',
            'This is an example hook for a animated captions video',
        ],
    },
];

const DURATIONS = [
    { value: 15, label: '15 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' },
];

const AI_DURATIONS = [4, 6, 8];

const BAD_RESPONSE = "The video service sent back something we couldn't play. Please try again.";
const VIDEO_BOXES = ['ftyp', 'moov', 'mdat', 'free', 'skip', 'wide'];

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const asBytes = (data) => {
    if (data instanceof ArrayBuffer) return new Uint8Array(data);
    if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    return null;
};

// The API answers with the mp4's bytes. Returns a playable URL, or null for an empty
// or unexpected answer (e.g. a JSON error, or JSON without a url).
const toVideoSrc = (data) => {
    const bytes = asBytes(data);
    if (!bytes || bytes.length === 0) return null;
    if (bytes[0] === 0x7b || bytes[0] === 0x5b) {
        try {
            const value = JSON.parse(new TextDecoder().decode(bytes));
            const item = Array.isArray(value) ? value[0] : Array.isArray(value?.data) ? value.data[0] : value;
            return typeof item?.url === 'string' && /^(https?:|data:video\/)/.test(item.url) ? item.url : null;
        } catch (e) {
            return null;
        }
    }
    // MP4/QuickTime start with a box such as ftyp at byte 4; WebM with the EBML header
    const box = String.fromCharCode(...bytes.slice(4, 8));
    const isWebm = bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3;
    if (!VIDEO_BOXES.includes(box) && !isWebm) return null;
    return URL.createObjectURL(new Blob([bytes], { type: 'video/mp4' }));
};

// "image/*,video/*" style accept lists, checked for dropped files (the picker filters by itself)
const accepts = (accept, file) => accept.split(',').some((pattern) => {
    const type = pattern.trim();
    return type.endsWith('/*') ? file.type.startsWith(type.slice(0, -1)) : file.type === type;
});

// Seconds from an FFmpeg "time=00:01:02.50" log line
const logSeconds = (message) => {
    const match = message.match(/time=(\d+):(\d+):(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) * 3600 + Number(match[2]) * 60 + parseFloat(match[3]) : null;
};

// A labelled drop target: drag a file in, or press the button to pick one
const FileDrop = ({ id, label, hint, error, file, setFile, accept, kind, onClear }) => {
    const [isOver, setIsOver] = useState(false);
    const [typeError, setTypeError] = useState('');
    const inputRef = useRef(null);
    const message = typeError || error;

    const pick = (picked) => {
        if (!picked) return;
        if (!accepts(accept, picked)) {
            setTypeError(`That file isn't ${kind === 'audio' ? 'an audio file' : kind === 'image' ? 'an image' : 'an image or video'}.`);
            return;
        }
        setTypeError('');
        setFile(picked);
    };

    return (
        <div className="ls-field">
            <span className="ls-label" id={`${id}-label`}>{label}</span>
            <div
                className={`ls-dropzone ls-media-drop${isOver ? ' is-over' : ''}${message ? ' ls-media-invalid' : ''}`}
                onClick={() => inputRef.current?.click()}
                onDragEnter={(e) => {
                    e.preventDefault();
                    setIsOver(true);
                }}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) setIsOver(false);
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    setIsOver(false);
                    pick(e.dataTransfer.files?.[0]);
                }}
            >
                <div className="ls-media-drop-text">
                    {file ? (
                        <span className="ls-chip is-medium">
                            <Icon name={kind === 'audio' ? 'music_note' : 'image'} size={16} />
                            <span className="ls-media-file-name">{file.name}</span>
                            {onClear && (
                                <button
                                    type="button"
                                    className="ls-chip-remove"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClear();
                                    }}
                                    aria-label={`Remove ${file.name}`}
                                >
                                    <Icon name="close" size={16} />
                                </button>
                            )}
                        </span>
                    ) : (
                        <>
                            <Icon name="upload_file" />
                            <span>Drop {kind === 'audio' ? 'an audio file' : kind === 'image' ? 'an image' : 'an image or video'} here</span>
                        </>
                    )}
                </div>
                <button type="button" className="ls-btn ls-btn-secondary" aria-describedby={`${id}-label`}>
                    {file ? 'Replace' : 'Choose file'}
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    hidden
                    aria-labelledby={`${id}-label`}
                    onChange={(e) => {
                        pick(e.target.files?.[0]);
                        e.target.value = '';
                    }}
                />
            </div>
            {(message || hint) && (
                <span className={`ls-hint${message ? ' ls-media-error' : ''}`} role={message ? 'alert' : undefined}>
                    {message || hint}
                </span>
            )}
        </div>
    );
};

const AudioPreview = ({ audioFile, audioUrl }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);

    // Presets play from their URL, uploads from a blob URL we own
    const src = useMemo(() => audioUrl || (audioFile ? URL.createObjectURL(audioFile) : null), [audioFile, audioUrl]);
    useEffect(() => () => {
        if (src && src.startsWith('blob:')) URL.revokeObjectURL(src);
    }, [src]);

    // Stop playback when the source changes
    useEffect(() => {
        audioRef.current?.pause();
        setIsPlaying(false);
    }, [src]);

    if (!src) return null;

    const handlePlayPause = async () => {
        const audio = audioRef.current;
        if (!audio) return;
        try {
            if (isPlaying) {
                audio.pause();
                setIsPlaying(false);
            } else {
                await audio.play();
                setIsPlaying(true);
            }
        } catch (error) {
            console.error('Audio playback error:', error);
            setIsPlaying(false);
        }
    };

    return (
        <>
            <audio ref={audioRef} src={src} preload="metadata" onEnded={() => setIsPlaying(false)} />
            <button type="button" className="ls-btn ls-btn-secondary" onClick={handlePlayPause} aria-pressed={isPlaying}>
                <Icon name={isPlaying ? 'pause' : 'play_arrow'} size={18} />
                {isPlaying ? 'Pause' : 'Play'} track
            </button>
        </>
    );
};

// An AI video prompt from the script: the scenes of its first beats
const promptFromScript = (script) => {
    if (!script) return '';
    const scenes = script.beats.slice(0, 3).map(beat => beat.visual.trim()).filter(Boolean);
    return scenes.length ? `Vertical short-form video. ${scenes.join(' Then ')}` : '';
};

const VideoScreen = () => {
    const { embedded, seed } = useCreateContext();
    const { flow, report } = useCreateFlow();
    const [videoTypeIndex, setVideoTypeIndex] = useState(0);
    // The picked hook goes on the video; it follows later picks until you type your own
    const [videoText, setVideoText] = useState(() => flow.hook || seed?.hook || '');
    usePrefill(flow.hook, videoText, setVideoText);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [image, setImage] = useState(null);
    const [audio, setAudio] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    // The browser can't play the video (e.g. no H.264); it can still be downloaded
    const [previewFailed, setPreviewFailed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [stage, setStage] = useState('');
    const [progress, setProgress] = useState(0);
    const [audioSource, setAudioSource] = useState('select');
    const [videoDuration, setVideoDuration] = useState(30);
    const [demoMedia, setDemoMedia] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    // 'template' builds the video in the browser with FFmpeg, 'ai' generates it with an AI video model
    const [mode, setMode] = useState('template');
    const [aiPrompt, setAiPrompt] = useState('');
    usePrefill(promptFromScript(flow.script), aiPrompt, setAiPrompt);
    const [aiImage, setAiImage] = useState(null);
    const [aiAspectRatio, setAiAspectRatio] = useState('9:16');
    const [aiDuration, setAiDuration] = useState(8);
    const [aiPremium, setAiPremium] = useState(false);
    const [aiAudio, setAiAudio] = useState(true);
    // Length of the video being rendered, for the progress bar
    const renderSecondsRef = useRef(0);

    const videoType = VIDEO_TYPES[videoTypeIndex] || VIDEO_TYPES[0];

    const clearFieldError = (field) => setFieldErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev));

    // The Caption and Review steps show the video
    useEffect(() => {
        report('video', { video: videoUrl || '' }, Boolean(videoUrl));
    }, [videoUrl, report]);

    // Free the previous video's blob when it's replaced
    useEffect(() => () => {
        if (videoUrl && videoUrl.startsWith('blob:')) URL.revokeObjectURL(videoUrl);
    }, [videoUrl]);

    // Track FFmpeg's progress through the final render
    useEffect(() => {
        const handleLog = ({ message }) => {
            console.log('FFmpeg Log:', message);
            const seconds = logSeconds(message);
            if (seconds !== null && renderSecondsRef.current > 0) {
                setProgress(Math.min((seconds / renderSecondsRef.current) * 100, 100));
            }
        };
        ffmpeg.on('log', handleLog);
        return () => ffmpeg.off('log', handleLog);
    }, []);

    const handleGenerateAiVideo = async () => {
        if (!aiPrompt.trim()) {
            setFieldErrors({ aiPrompt: 'Describe the video you want.' });
            return;
        }

        setFieldErrors({});
        setError('');
        setNotice('');
        setStage('ai');
        setIsLoading(true);
        try {
            const data = await api.generateVideo({
                prompt: aiPrompt,
                aspectRatio: aiAspectRatio,
                duration: aiDuration,
                image: aiImage ? await readAsDataUrl(aiImage) : undefined,
                premium: aiPremium,
                generateAudio: aiAudio,
            });
            const url = toVideoSrc(data);
            if (!url) {
                setError(BAD_RESPONSE);
                return;
            }
            setPreviewFailed(false);
            setVideoUrl(url);
        } catch (err) {
            if (isLimitError(err)) return;
            setError("Couldn't generate the AI video. Please try again.");
        } finally {
            setIsLoading(false);
            setStage('');
        }
    };

    const handleGenerateVideo = async () => {
        const missing = {
            videoText: videoText.trim() ? '' : 'Add the text to show on the video.',
            image: image ? '' : 'Add a background image.',
            audio: audio ? '' : 'Pick a track or upload your own audio.',
        };
        setFieldErrors(missing);
        if (missing.videoText || missing.image || missing.audio) return;

        setError('');
        setNotice('');
        renderSecondsRef.current = 0;
        let step = 'engine';
        try {
            setIsLoading(true);
            setProgress(0);

            // Load FFmpeg if not already loaded
            if (!loaded) {
                setStage('engine');
                const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.4/dist/umd';
                await ffmpeg.load({
                    coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
                    wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
                    log: true,
                });
                loaded = true;
            }
            step = 'render';
            setStage('preparing');

            // Write input files
            const imageExt = image.name.split('.').pop().toLowerCase();
            const inputImageName = `input.${imageExt}`;
            await ffmpeg.writeFile(inputImageName, await fetchFile(image));

            // Handle audio input
            let audioData;
            if (audioUrl) {
                // For preset audio files
                const response = await fetch(audioUrl);
                audioData = new Uint8Array(await response.arrayBuffer());
            } else {
                // For uploaded audio files
                audioData = await fetchFile(audio);
            }
            await ffmpeg.writeFile('input.mp3', audioData);

            // Convert audio to WAV
            await ffmpeg.exec([
                '-i', 'input.mp3',
                '-acodec', 'pcm_s16le',
                '-ar', '44100',
                'temp.wav',
            ]);

            // Write the font file
            await ffmpeg.writeFile('font.ttf', await fetchFile(fontFile));

            // Create text overlay
            const escapedText = videoText.replace(/['[\],]/g, '\\$&');
            const drawTextFilter = `drawtext=text='${escapedText}':fontcolor=white:fontsize=60:box=1:boxcolor=black@0.5:boxborderw=5:x=(w-text_w)/2:y=(h-text_h)/2:fontfile=font.ttf`;

            let ffmpegArgs;
            let useDemoMedia = demoMedia;

            if (useDemoMedia) {
                try {
                    const demoExt = demoMedia.name.split('.').pop().toLowerCase();
                    const demoInputName = `demo.${demoExt}`;
                    await ffmpeg.writeFile(demoInputName, await fetchFile(demoMedia));

                    const filterComplex = `[0:v]scale=1080:1920:force_original_aspect_ratio=1,pad=1080:1920:(ow-iw)/2:(oh-ih)/2,${drawTextFilter}[main];` +
                        `[2:v]scale=1080:1920:force_original_aspect_ratio=1,pad=1080:1920:(ow-iw)/2:(oh-ih)/2[demo];` +
                        `[main][1:a][demo][1:a]concat=n=2:v=1:a=1[outv][outa]`;

                    ffmpegArgs = [
                        '-loop', '1',
                        '-i', inputImageName,
                        '-i', 'temp.wav',
                        '-i', demoInputName,
                        '-filter_complex', filterComplex,
                        '-map', '[outv]',
                        '-map', '[outa]',
                        '-c:v', 'libx264',
                        '-preset', 'ultrafast',
                        '-t', (videoDuration * 2).toString(),
                        '-r', '30',
                        '-pix_fmt', 'yuv420p',
                        '-c:a', 'aac',
                        '-strict', 'experimental',
                        '-b:a', '192k',
                        '-shortest',
                        '-y',
                        'output.mp4',
                    ];
                    renderSecondsRef.current = videoDuration * 2;
                } catch (demoError) {
                    console.error('Error processing demo media:', demoError);
                    setNotice("Couldn't use the demo media, so the video was made without it.");
                    useDemoMedia = null;
                }
            }

            if (!useDemoMedia) {
                ffmpegArgs = [
                    '-loop', '1',
                    '-i', inputImageName,
                    '-i', 'temp.wav',
                    '-vf', `scale=1080:1920:force_original_aspect_ratio=1,pad=1080:1920:(ow-iw)/2:(oh-ih)/2,${drawTextFilter}`,
                    '-c:v', 'libx264',
                    '-preset', 'ultrafast',
                    '-t', videoDuration.toString(),
                    '-r', '30',
                    '-pix_fmt', 'yuv420p',
                    '-c:a', 'aac',
                    '-strict', 'experimental',
                    '-b:a', '192k',
                    '-shortest',
                    '-y',
                    'output.mp4',
                ];
                renderSecondsRef.current = videoDuration;
            }

            // Execute FFmpeg command
            setStage('rendering');
            await ffmpeg.exec(ffmpegArgs);

            // Read and process output
            const data = await ffmpeg.readFile('output.mp4');
            if (!data || data.length === 0) {
                throw new Error('Generated video file is empty');
            }

            setPreviewFailed(false);
            setVideoUrl(URL.createObjectURL(new Blob([data.buffer], { type: 'video/mp4' })));
        } catch (err) {
            console.error('FFmpeg error:', err);
            if (step === 'engine') {
                setError("Couldn't load the video engine. Check your connection and try again.");
            } else {
                const details = err?.message || String(err);
                setError(`Couldn't render the video: ${details}${err?.stderr ? `\nDetails: ${err.stderr}` : ''}`);
            }
        } finally {
            renderSecondsRef.current = 0;
            setIsLoading(false);
            setStage('');
            setProgress(0);
        }
    };

    const handlePresetAudioChange = async (selectedUrl) => {
        try {
            if (!selectedUrl) {
                setAudio(null);
                setAudioUrl(null);
                return;
            }

            const response = await fetch(selectedUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const blob = await response.blob();
            const file = new File([blob], selectedUrl.split('/').pop(), { type: 'audio/mpeg' });
            setAudio(file);
            setAudioUrl(selectedUrl);
            clearFieldError('audio');
        } catch (err) {
            console.error('Error loading preset audio:', err);
            setFieldErrors(prev => ({ ...prev, audio: "Couldn't load that track. Try another one, or upload your own audio." }));
            setAudioSource('select');
            setAudio(null);
            setAudioUrl(null);
        }
    };

    const clearAudio = () => {
        setAudio(null);
        setAudioUrl(null);
        setAudioSource('select');
    };

    const statusText = {
        ai: 'Generating with AI... this can take a few minutes',
        engine: 'Loading the video engine...',
        preparing: 'Preparing your files...',
        rendering: `Rendering... ${Math.round(progress)}%`,
    }[stage] || 'Working...';

    const frameClass = mode === 'ai' && aiAspectRatio === '16:9' ? 'ls-media ls-video-wide' : 'ls-media is-video ls-video-frame';

    return (
        <div className="ls-col">
            {!embedded && (
                <div className="ls-page-header">
                    <h1 className="ls-page-title">Video generator</h1>
                </div>
            )}

            <div className="ls-two-col">
                <div className="ls-card">
                    <div className="ls-card-header" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className="ls-card-title">Video settings</span>
                        <div className="ls-segmented" role="group" aria-label="How to make the video">
                            <button type="button" aria-pressed={mode === 'template'} onClick={() => setMode('template')} disabled={isLoading}>
                                Template
                            </button>
                            <button type="button" aria-pressed={mode === 'ai'} onClick={() => setMode('ai')} disabled={isLoading}>
                                AI video
                            </button>
                        </div>
                    </div>
                    <div className="ls-card-sub">
                        {mode === 'ai'
                            ? 'Describe a scene and an AI video model films it.'
                            : 'Put your text over an image with music. Rendered in your browser.'}
                    </div>

                    {mode === 'ai' ? (
                        <div className="ls-form">
                            <label className="ls-field">
                                <span className="ls-label">Describe the video</span>
                                <textarea
                                    className={`ls-textarea${fieldErrors.aiPrompt ? ' ls-media-invalid' : ''}`}
                                    rows={5}
                                    value={aiPrompt}
                                    onChange={(e) => {
                                        setAiPrompt(e.target.value);
                                        if (e.target.value.trim()) clearFieldError('aiPrompt');
                                    }}
                                    placeholder="e.g. Slow push-in on a nurse opening a meal-prep box in a break room, warm light, she smiles at the camera"
                                    aria-invalid={Boolean(fieldErrors.aiPrompt)}
                                />
                                <span className={`ls-hint${fieldErrors.aiPrompt ? ' ls-media-error' : ''}`} role={fieldErrors.aiPrompt ? 'alert' : undefined}>
                                    {fieldErrors.aiPrompt || 'The scene, camera movement, and any dialogue or sound.'}
                                </span>
                            </label>

                            <div className="ls-form-grid">
                                <label className="ls-field">
                                    <span className="ls-label">Shape</span>
                                    <select className="ls-select" value={aiAspectRatio} onChange={(e) => setAiAspectRatio(e.target.value)}>
                                        <option value="9:16">9:16 (TikTok, Reels, Shorts)</option>
                                        <option value="16:9">16:9 (YouTube)</option>
                                    </select>
                                </label>
                                <label className="ls-field">
                                    <span className="ls-label">Length</span>
                                    <select className="ls-select" value={aiDuration} onChange={(e) => setAiDuration(Number(e.target.value))}>
                                        {AI_DURATIONS.map(seconds => <option key={seconds} value={seconds}>{seconds} seconds</option>)}
                                    </select>
                                </label>
                            </div>

                            <FileDrop
                                id="ls-video-ai-image"
                                label="Starting frame (optional)"
                                hint="Animate an image, e.g. your AI model or a product shot."
                                kind="image"
                                accept="image/*"
                                file={aiImage}
                                setFile={setAiImage}
                                onClear={() => setAiImage(null)}
                            />

                            <div className="ls-field">
                                <span className="ls-label">Options</span>
                                <div className="ls-choices">
                                    <label className="ls-choice">
                                        <input type="checkbox" checked={aiAudio} onChange={(e) => setAiAudio(e.target.checked)} />
                                        Generate audio
                                    </label>
                                    <label className="ls-choice">
                                        <input type="checkbox" checked={aiPremium} onChange={(e) => setAiPremium(e.target.checked)} />
                                        Premium quality
                                    </label>
                                </div>
                                <span className="ls-hint">Premium is part of the Scale plan and takes longer.</span>
                            </div>
                        </div>
                    ) : (
                        <div className="ls-form">
                            <div className="ls-form-grid">
                                <label className="ls-field">
                                    <span className="ls-label">Format</span>
                                    <select className="ls-select" value={videoTypeIndex} onChange={(e) => setVideoTypeIndex(Number(e.target.value))}>
                                        {VIDEO_TYPES.map((type, index) => <option key={type.title} value={index}>{type.title}</option>)}
                                    </select>
                                </label>
                                <label className="ls-field">
                                    <span className="ls-label">Length</span>
                                    <select className="ls-select" value={videoDuration} onChange={(e) => setVideoDuration(Number(e.target.value))}>
                                        {DURATIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </select>
                                </label>
                            </div>

                            <label className="ls-field">
                                <span className="ls-label">Text on the video</span>
                                <input
                                    className={`ls-input${fieldErrors.videoText ? ' ls-media-invalid' : ''}`}
                                    type="text"
                                    value={videoText}
                                    onChange={(e) => {
                                        setVideoText(e.target.value);
                                        if (e.target.value.trim()) clearFieldError('videoText');
                                    }}
                                    placeholder={videoType.exampleHooks[0]}
                                    aria-invalid={Boolean(fieldErrors.videoText)}
                                />
                                <span className={`ls-hint${fieldErrors.videoText ? ' ls-media-error' : ''}`} role={fieldErrors.videoText ? 'alert' : undefined}>
                                    {fieldErrors.videoText || `${videoType.description} ${videoType.hook}`}
                                </span>
                            </label>

                            <FileDrop
                                id="ls-video-image"
                                label="Background image"
                                hint="Shown behind your text, fitted to 9:16."
                                kind="image"
                                accept="image/*"
                                file={image}
                                error={fieldErrors.image}
                                setFile={(file) => {
                                    setImage(file);
                                    clearFieldError('image');
                                }}
                                onClear={() => setImage(null)}
                            />

                            <div className="ls-field">
                                <label className="ls-label" htmlFor="ls-video-track">Music</label>
                                <div className="ls-media-actions">
                                    <select
                                        id="ls-video-track"
                                        className="ls-select"
                                        style={{ flex: '1 1 200px', width: 'auto' }}
                                        value={audioSource}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setAudioSource(value);
                                            const option = PRESET_AUDIO.find(opt => opt.id === value);
                                            handlePresetAudioChange(option?.url);
                                        }}
                                    >
                                        {PRESET_AUDIO.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
                                    </select>
                                    <AudioPreview audioFile={audio} audioUrl={audioUrl} />
                                </div>
                            </div>

                            <FileDrop
                                id="ls-video-audio"
                                label="Or upload your own audio"
                                hint="MP3 or WAV. Used instead of the track above."
                                kind="audio"
                                accept="audio/*"
                                file={audioUrl ? null : audio}
                                error={fieldErrors.audio}
                                setFile={(file) => {
                                    setAudio(file);
                                    setAudioUrl(null);
                                    setAudioSource('select');
                                    clearFieldError('audio');
                                }}
                                onClear={clearAudio}
                            />

                            <FileDrop
                                id="ls-video-demo"
                                label="Demo media (optional)"
                                hint="An image or video shown at the end."
                                kind="media"
                                accept="image/*,video/*"
                                file={demoMedia}
                                setFile={setDemoMedia}
                                onClear={() => setDemoMedia(null)}
                            />
                        </div>
                    )}

                    <button
                        type="button"
                        className={`ls-btn ${videoUrl ? 'ls-btn-secondary' : 'ls-btn-primary'}`}
                        onClick={mode === 'ai' ? handleGenerateAiVideo : handleGenerateVideo}
                        disabled={isLoading}
                        style={{ alignSelf: 'flex-start', marginTop: 4 }}
                    >
                        {isLoading ? <span className="ls-spinner" /> : <Icon name={mode === 'ai' ? 'auto_awesome' : 'movie'} size={16} />}
                        {isLoading ? 'Generating...' : videoUrl ? 'Generate again' : 'Generate video'}
                    </button>
                </div>

                <div className="ls-card">
                    <div className="ls-card-header">
                        <span className="ls-card-title">Preview</span>
                        {videoUrl && !isLoading ? (
                            <a className="ls-btn ls-btn-pill" href={videoUrl} download="video.mp4">
                                <Icon name="download" size={14} />Download
                            </a>
                        ) : (
                            <button type="button" className="ls-btn ls-btn-pill" disabled>
                                <Icon name="download" size={14} />Download
                            </button>
                        )}
                    </div>
                    {error && <Alert severity="error"><span style={{ whiteSpace: 'pre-line' }}>{error}</span></Alert>}
                    {notice && <Alert severity="warning">{notice}</Alert>}
                    <div className={frameClass} aria-busy={isLoading}>
                        {isLoading ? (
                            <>
                                <div className="ls-skeleton ls-media-fill" />
                                <div className="ls-media-status" role="status">
                                    <div className="ls-media-status-row">
                                        <span className="ls-spinner" />{statusText}
                                    </div>
                                    {stage === 'rendering' && (
                                        <div className="ls-progress"><div style={{ width: `${progress}%` }} /></div>
                                    )}
                                </div>
                            </>
                        ) : videoUrl && !previewFailed ? (
                            <video controls src={videoUrl} onError={() => setPreviewFailed(true)} />
                        ) : videoUrl ? (
                            <div className="ls-placeholder ls-media-fill">
                                <Icon name="videocam_off" />
                                This browser can't play the preview. Download the video to watch it.
                            </div>
                        ) : (
                            <div className="ls-placeholder ls-media-fill">
                                <Icon name="movie" />
                                Your video will appear here
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoScreen;
